import React, { useState } from "react";
import { useI18n } from "../lib/i18n";
import { apiClient } from "../lib/api";
import {
  MessageCircle, Twitter, Facebook, Send, Mail, Link as LinkIcon, Check, Share2,
} from "lucide-react";

/**
 * ShareBar — "أعجبني في كارتال" style social share strip for any listing.
 *
 * Props:
 *   url:        full URL to share (defaults to window.location.href)
 *   title:      short title used as the share line
 *   price:      optional SAR price to append
 *   city:       optional city name
 *   vehicleId:  optional listing id — used both as utm_content and in
 *               the analytics ping (so we can compute "top-shared" stats).
 */
export default function ShareBar({ url, title, price, city, vehicleId }) {
  const { lang } = useI18n();
  const [copied, setCopied] = useState(false);
  const [nativeBusy, setNativeBusy] = useState(false);

  // Build the canonical sharable URL with UTM params per channel
  const baseUrl = url || `https://cartal.sa/api/share/${lang}`;

  // iter330 — Outbound-click pings removed (Inspect→Diagnose→Fix→Verify).
  //
  // Root cause of double-counting: pinging /analytics/share-visits on
  // the SELLER's tap recorded a "visit" before any recipient had
  // clicked the link, and then the recipient's own click (which lands
  // on a URL carrying ?utm_source=<channel>) triggered a SECOND row
  // via useShareVisitTracker — inflating every channel's count by ~2×.
  //
  // Strict policy per product spec ("عدم احتساب الزيارة مرتين" +
  // "لا تخمين للمصدر"): a "visit" is only recorded when a real page
  // load happens with a confirmed source (utm_source on inbound URL
  // OR classifiable Referer). Outbound clicks are intent, not visits.
  //
  // We keep the function as a no-op placeholder to avoid touching the
  // call-sites' shape (copy/native both still work — their outbound
  // URLs already carry utm_source and will attribute correctly when
  // the recipient opens them).
  const pingShare = (_channelId) => { /* no-op — see iter330 comment */ };

  const buildUrl = (source) => {
    try {
      const u = new URL(baseUrl);
      u.searchParams.set("utm_source", source);
      u.searchParams.set("utm_medium", "social");
      u.searchParams.set("utm_campaign", "share_listing");
      if (vehicleId) u.searchParams.set("utm_content", vehicleId);
      return u.toString();
    } catch {
      const sep = baseUrl.includes("?") ? "&" : "?";
      return `${baseUrl}${sep}utm_source=${encodeURIComponent(source)}&utm_medium=social&utm_campaign=share_listing${vehicleId ? `&utm_content=${encodeURIComponent(vehicleId)}` : ""}`;
    }
  };

  // Build the "أعجبني في كارتال" share line
  const liked = lang === "ar" ? "أعجبني في كارتال:" : "I liked this on CARTAL:";
  const pricePart = price ? ` · ${Number(price).toLocaleString()} ${lang === "ar" ? "ريال" : "SAR"}` : "";
  const cityPart  = city  ? ` · ${city}` : "";
  const text  = `${liked} ${title}${pricePart}${cityPart}`;
  const enc   = encodeURIComponent;

  // Per-channel URLs — each gets a unique utm_source
  const waUrl       = buildUrl("whatsapp");
  const xUrl        = buildUrl("x");
  const tgUrl       = buildUrl("telegram");
  const fbUrl       = buildUrl("facebook");
  const emailUrl    = buildUrl("email");
  const copyUrl     = buildUrl("copy");
  const nativeUrl   = buildUrl("native");

  const channels = [
    {
      id: "whatsapp", ar: "واتساب", en: "WhatsApp", icon: MessageCircle,
      color: "#25D366",
      href: `https://wa.me/?text=${enc(text + "\n" + waUrl)}`,
    },
    {
      id: "x", ar: "إكس (تويتر)", en: "X (Twitter)", icon: Twitter,
      color: "#0a0a0a",
      href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(xUrl)}`,
    },
    {
      id: "telegram", ar: "تليجرام", en: "Telegram", icon: Send,
      color: "#0088cc",
      href: `https://t.me/share/url?url=${enc(tgUrl)}&text=${enc(text)}`,
    },
    {
      id: "facebook", ar: "فيسبوك", en: "Facebook", icon: Facebook,
      color: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(fbUrl)}&quote=${enc(text)}`,
    },
    {
      id: "email", ar: "البريد", en: "Email", icon: Mail,
      color: "#525252",
      href: `mailto:?subject=${enc(text)}&body=${enc(text + "\n" + emailUrl)}`,
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyUrl);
      setCopied(true);
      pingShare("copy");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard may be blocked — silent fallback */
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    setNativeBusy(true);
    try {
      await navigator.share({ title: text, text, url: nativeUrl });
      pingShare("native");
    } catch {
      /* user cancelled */
    } finally {
      setNativeBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#0a0a0a]/10 bg-[#fafaf7] p-4" data-testid="share-bar">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="overline overline-gold inline-flex items-center gap-1.5">
          <Share2 className="w-3.5 h-3.5" />
          {lang === "ar" ? "أعجبني في كارتال — شارك الإعلان" : "I liked this on CARTAL — share"}
        </div>
        {typeof navigator !== "undefined" && navigator.share && (
          <button
            type="button"
            onClick={nativeShare}
            disabled={nativeBusy}
            className="text-xs font-semibold text-[#8a6f2e] hover:text-[#5a4615] inline-flex items-center gap-1"
            data-testid="share-native"
          >
            <Share2 className="w-3.5 h-3.5" />
            {lang === "ar" ? "خيارات أكثر" : "More"}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {channels.map((c) => {
          const Icon = c.icon;
          return (
            <a
              key={c.id}
              href={c.href}
              onClick={() => pingShare(c.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold bg-white border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] hover:bg-[#fdf9ef] transition-colors"
              data-testid={`share-${c.id}`}
              aria-label={lang === "ar" ? c.ar : c.en}
            >
              <Icon className="w-4 h-4" style={{ color: c.color }} />
              <span>{lang === "ar" ? c.ar : c.en}</span>
            </a>
          );
        })}
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold bg-white border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] hover:bg-[#fdf9ef] transition-colors"
          data-testid="share-copy"
        >
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <LinkIcon className="w-4 h-4 text-[#525252]" />}
          {copied
            ? (lang === "ar" ? "تم النسخ" : "Copied")
            : (lang === "ar" ? "نسخ الرابط" : "Copy link")}
        </button>
      </div>
    </div>
  );
}
